function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return _typeof(key) === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (_typeof(input) !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (_typeof(res) !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import PropTypes from "prop-types";
import React from "react";
import cx from "classnames";
import createUrl from "../../utilities/createUrl/index.js";
import createTransformationQuery from "../../utilities/createTransformationQuery/index.js";
import { useImagePreloadRenderer } from "../ImagePreloadExtractor/index.js";
import intrinsicDimensionsFromProps from "../../utilities/intrinsicDimensionsFromProps/index.js";
import transformationsShape from "../transformationsShape.js";

/**
 * Render an img element. Can be used by itself or as a child to `Picture`
 */
var Img = function Img(props) {
  var src = createUrl(props);
  var lazy = props.lazy,
    alt = props.alt,
    preload = props.preload,
    onLoad = props.onLoad,
    givenRenderPreload = props.renderPreload,
    decoding = props.decoding,
    className = props.className,
    elementProps = props.elementProps;
  var _intrinsicDimensionsF = intrinsicDimensionsFromProps(props),
    width = _intrinsicDimensionsF.width,
    height = _intrinsicDimensionsF.height;
  var srcProp = lazy ? 'data-src' : 'src';
  var lazyClassName = lazy ? 'lazyload' : '';
  var fullSrc = "".concat(src).concat(createTransformationQuery(props));
  var imgProps = _defineProperty({}, srcProp, fullSrc);
  var classes = cx(lazyClassName, className);
  var renderPreload = useImagePreloadRenderer(givenRenderPreload);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("img", _extends({}, elementProps, imgProps, {
    width: width,
    height: height,
    className: classes,
    alt: alt,
    decoding: decoding,
    onLoad: onLoad
  })), lazy && /*#__PURE__*/React.createElement("noscript", null, /*#__PURE__*/React.createElement("img", {
    alt: alt,
    src: fullSrc,
    width: width,
    height: height
  })), preload && renderPreload({
    rel: 'preload',
    as: 'image',
    href: fullSrc
  }));
};
Img.defaultProps = {
  className: '',
  lazy: false,
  elementProps: {},
  alt: '',
  decoding: 'async'
};
process.env.NODE_ENV !== "production" ? Img.propTypes = {
  /**
   * Preload image. Requires setting up a way to extract preloads. Is only allowed on this element when used without the parent `Picture` component
   */
  preload: PropTypes.bool,
  /**
   * Override how preloads are rendered
   */
  renderPreload: PropTypes.func,
  /**
   * Call a callback when the image is loaded.
   */
  onLoad: PropTypes.func,
  /**
   * Enable lazy loading
   */
  lazy: PropTypes.bool,
  /**
   * Pass additional props to underlying element
   */
  elementProps: PropTypes.shape({}),
  /**
   * Extend class name
   */
  className: PropTypes.string,
  /**
   * Necessary for accessibility
   */
  alt: PropTypes.string,
  /**
   * Decoding strategy. Async decoding improves render performance though it causes images to be gradually filled in instead of painted all at once.
   */
  decoding: PropTypes.oneOf(['async', 'auto', 'sync']),
  /**
   * Set the desired displayed width of the image
   */
  width: PropTypes.number,
  /**
   * Set the desired displayed height of the image
   */
  height: PropTypes.number,
  /**
   * A 4 element tuple with the crop coordinates (`[x1, y1, x2, y2]`)
   * to pass to the media API. [See Media API's documentation](https://api-docs.eng.xogrp.com/details/production-media-api)
   */
  crop: PropTypes.arrayOf(PropTypes.number),
  /**
   * Enable smartCrop as the sizing strategy. [See Media API's documentation](https://api-docs.eng.xogrp.com/details/production-media-api)
   */
  smartCrop: PropTypes.bool,
  /**
   * Force image to fit in given dimensions (Only makes sense when height and width are provided).
   *
   * [See Media API's documentation](https://api-docs.eng.xogrp.com/details/production-media-api)
   */
  fit: PropTypes.bool,
  /**
   * Number of degrees to rotate the image
   *
   * [See Media API's documentation](https://api-docs.eng.xogrp.com/details/production-media-api)
   */
  rotate: PropTypes.number,
  /**
   * Add a slug to the image url. [See Media API's documentation](https://api-docs.eng.xogrp.com/details/production-media-api)
   */
  slug: PropTypes.string,
  /**
   * Override transformations. If array is provided, transformations are applied in the given order. Transformations shape includes
   *
   * | name | type |
   * |------|------|
   * | width | `number` |
   * | height | `number` |
   * | rotate | `number` |
   * | crop | `[x1: number, y1: number, x2: number, y2: number]` |
   * | fit | `bool` |
   * | smartCrop | `bool` |
   */
  transformations: PropTypes.oneOfType([transformationsShape, PropTypes.arrayOf(transformationsShape)])
} : void 0;

// eslint-disable-next-line no-underscore-dangle
Img.__isImgNode = true;
export default Img;